import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Space, Table, Tag } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const PmvReliabilityConditionPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['pmv-reliability-conditions'],
    queryFn: () => pmvService.listReliabilityConditions({ limit: 100 }),
  });

  const items = q.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Condition Monitoring"
        subtitle="Condition events from indicators — sensor faults ≠ equipment health"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/reliability/control-tower')}>
              Control Tower
            </Button>
            <Button onClick={() => navigate('/asset/pmv/reliability')}>Health Register</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/attention')}>Attention</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/failures')}>Failures</Button>
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Sensor faults and unreliable readings are not the same as equipment health — condition events stay evidence-first."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No condition events recorded" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1000 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Equipment',
              dataIndex: 'equipment_label',
              width: 200,
              ellipsis: true,
              render: (v, r) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}
                  >
                    {v || 'Equipment'}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Indicator',
              dataIndex: 'indicator_code',
              width: 160,
              ellipsis: true,
            },
            {
              title: 'Event',
              dataIndex: 'event_type',
              width: 180,
              render: (v) => (
                <Tag
                  color={
                    v?.includes('CRITICAL') || v === 'SENSOR_UNRELIABLE' ? 'red' : 'orange'
                  }
                >
                  {v}
                </Tag>
              ),
            },
            {
              title: 'Value',
              dataIndex: 'observed_value',
              width: 100,
              render: (v) => (v != null ? String(v) : '—'),
            },
            {
              title: 'Quality',
              dataIndex: 'quality',
              width: 110,
              render: (v) => v || '—',
            },
            {
              title: 'Effective',
              dataIndex: 'effective_at',
              width: 170,
              render: (v) => (v ? new Date(v).toLocaleString() : '—'),
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvReliabilityConditionPage;
